import { User, sendEmailVerification } from 'firebase/auth';

/** Where an email/password signup should go once the address is verified. */
export const POST_VERIFY_PATH_KEY = 'motzu.postVerifyPath';

/** Set when Firebase accepts a verification email, so the verify page does not send a second one. */
const VERIFICATION_SENT_AT_KEY = 'motzu.verificationEmailSentAt';

const RECENT_SEND_MS = 60_000;

let inFlightSend: Promise<void> | null = null;

/** Email/password accounts stay blocked until Firebase marks the address verified. Google accounts already are. */
export function needsEmailVerification(user: { emailVerified: boolean } | null | undefined): boolean {
  return !!user && user.emailVerified === false;
}

/** Same-app path only. A stored value that is not one of these falls back to the dashboard. */
export function isSafeAppPath(path: string): boolean {
  return /^\/[^/]/.test(path);
}

export function rememberPostVerifyPath(path: string): void {
  if (!isSafeAppPath(path)) return;
  sessionStorage.setItem(POST_VERIFY_PATH_KEY, path);
}

export function peekPostVerifyPath(): string {
  const stored = sessionStorage.getItem(POST_VERIFY_PATH_KEY);
  if (stored && isSafeAppPath(stored)) return stored;
  return '/app/dashboard';
}

export function verificationEmailSentRecently(): boolean {
  const sentAt = Number(sessionStorage.getItem(VERIFICATION_SENT_AT_KEY) || 0);
  return sentAt > 0 && Date.now() - sentAt < RECENT_SEND_MS;
}

/**
 * Firebase hosts the action page (`handleCodeInApp: false`), same as password reset,
 * then returns the browser to this app.
 */
export function verificationActionCodeSettings(): { url: string; handleCodeInApp: false } {
  return {
    url: `${window.location.origin}/verify-email`,
    handleCodeInApp: false,
  };
}

export async function sendAccountVerificationEmail(user: User): Promise<void> {
  if (inFlightSend) return inFlightSend;
  inFlightSend = (async () => {
    await sendEmailVerification(user, verificationActionCodeSettings());
    sessionStorage.setItem(VERIFICATION_SENT_AT_KEY, String(Date.now()));
  })().finally(() => {
    inFlightSend = null;
  });
  return inFlightSend;
}
